import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../components/SafeIcon';
import { supabase } from '@/integrations/supabase/client';
import * as FiIcons from 'react-icons/fi';
import snapChartLogo from '@/assets/snap-chart-logo.png';
import ProfileModal from '../components/ProfileModal';
import SecurityModal from '../components/SecurityModal';
import TrialBanner from '../components/TrialBanner';
import KYCVerificationModal from '../components/kyc/KYCVerificationModal';
import OnboardingFlow from '../components/onboarding/OnboardingFlow';

const {
  FiUser, FiSettings, FiTrendingUp, FiDollarSign, FiActivity, FiShield,
  FiBarChart2, FiPieChart, FiTarget, FiClock, FiCheck, FiX, FiEye,
  FiLock, FiKey, FiUserCheck, FiUsers, FiBook, FiMonitor, FiGlobe,
  FiDatabase, FiWifi, FiZap, FiChevronRight, FiCpu, FiLayers, FiFolder, FiShare2
} = FiIcons;

const Dashboard = () => {
  const { user } = useAuth();
  const { profile, usage, isAdmin, loading } = useSubscription();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [capitalPreserved, setCapitalPreserved] = useState(null); // { totalUsd, totalR, decisions }
  const [viralStats, setViralStats] = useState(null); // { shares, clicks, signups, top_channel }
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showAdminTools, setShowAdminTools] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [showSecurityModal, setShowSecurityModal] = useState(false);
  const [showKYCModal, setShowKYCModal] = useState(false);
  const [hasDataRoomAccess, setHasDataRoomAccess] = useState(false);
  const [isCTOManager, setIsCTOManager] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordStatus, setPasswordStatus] = useState(null);
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Check if user has access to any data rooms
  useEffect(() => {
    const checkDataRoomAccess = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase.rpc('get_user_data_rooms');
        console.log('📂 Data room check:', data, error);
        if (!error && data?.success && data?.data_rooms?.length > 0) {
          setHasDataRoomAccess(true);
        }
      } catch (err) {
        console.error('Error checking data room access:', err);
      }
    };
    checkDataRoomAccess();
  }, [user]);

  // Fetch the total Capital Preserved by AI for this user
  // (sum of capital_preserved_usd across COMPLETED counterfactual records).
  // Populated by the Vercel cron robot api/snapshot-counterfactuals; on cold
  // start the total may be 0 until the first cron run.
  useEffect(() => {
    const fetchCapitalPreserved = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase
          .from('counterfactual_tracking')
          .select('capital_preserved_usd, capital_preserved_r')
          .eq('user_id', user.id)
          .eq('tracking_status', 'COMPLETED');
        if (error) { console.error('[dashboard] capital_preserved fetch:', error.message); return; }
        // Marketing rule: the "Capital Saved" card shows ONLY the WINS
        // (decisions where the AI was right to say NO-TRADE -> capital_preserved_usd > 0).
        // Net would include misses (the AI said WAIT and the trade would have won),
        // which can turn the headline number NEGATIVE -> kills conversion.
        // Per-trade card (CounterfactualProof) still shows the honest +/- per decision.
        const wins = (data || []).filter(r => Number(r.capital_preserved_usd) > 0);
        const decisions = wins.length;
        const totalUsd = wins.reduce((s, r) => s + Number(r.capital_preserved_usd), 0);
        const totalR = wins.reduce((s, r) => s + Number(r.capital_preserved_r), 0);
        setCapitalPreserved({ totalUsd, totalR, decisions });
      } catch (err) {
        console.error('[dashboard] capital_preserved error:', err);
      }
    };
    fetchCapitalPreserved();
  }, [user]);

  // Fetch the user's viral stats (shares / clicks / signups) for the
  // "Your Viral Impact" card. Lazy: the RPC also lazy-generates the
  // share_code via ensure_share_code on first share-button open.
  useEffect(() => {
    const fetchViralStats = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase.rpc('get_my_viral_stats');
        if (error) { console.error('[dashboard] viral_stats fetch:', error.message); return; }
        if (data) setViralStats(data);
      } catch (err) {
        console.error('[dashboard] viral_stats error:', err);
      }
    };
    fetchViralStats();
  }, [user]);

  // Lazy-generate (or fetch) the user's share_code for the "Copy your link"
  // button below the viral card.
  const [myShareCode, setMyShareCode] = useState(null);
  useEffect(() => {
    if (!user) return;
    supabase.rpc('ensure_share_code').then(({ data, error }) => {
      if (!error && typeof data === 'string') setMyShareCode(data);
    });
  }, [user]);

  const copyMyShareLink = () => {
    const link = `https://snap-chart.ai/?ref=${myShareCode || 'share'}`;
    try {
      navigator.clipboard.writeText(link);
      setShareLinkCopied(true);
      setTimeout(() => setShareLinkCopied(false), 2000);
    } catch (err) { console.error('Copy failed:', err); }
  };

  // Check if user is a CTO manager
  useEffect(() => {
    const checkCTOAccess = async () => {
      if (!user) return;
      try {
        const { data, error } = await supabase.rpc('is_cto_manager');
        if (!error && data === true) {
          setIsCTOManager(true);
        }
      } catch (err) {
        console.error('Error checking CTO access:', err);
      }
    };
    checkCTOAccess();
  }, [user]);

  // Fetch user dashboard stats
  useEffect(() => {
    const fetchStats = async () => {
      if (!user) return;

      try {
        console.log('📊 Fetching dashboard stats...');
        
        // Use get_journal_stats which exists
        const { data: journalStats, error: journalError } = await supabase
          .rpc('get_journal_stats', { p_user_id: user.id });

        if (!journalError && journalStats) {
          console.log('✅ Journal stats fetched:', journalStats);
          
          // Calculate win rate based on completed trades (WIN + LOSS), not all trades
          const completedTrades = (journalStats.wins || 0) + (journalStats.losses || 0);
          const winRate = completedTrades > 0 
            ? Math.round((journalStats.wins / completedTrades) * 100) 
            : 0;
          
          setStats({
            total_analyses: journalStats.total_analyses || 0,
            // Use executed_trades (WIN/LOSS/BREAKEVEN) not trade_decisions (all TRADE entries)
            total_trades: journalStats.executed_trades || 0,
            win_rate: winRate,
            total_pnl: journalStats.total_r_multiple || 0,
          });
        } else {
          console.log('⚠️ Journal stats failed, using usage count:', journalError?.message);
          setStats({
            total_analyses: usage?.count || 0,
            total_trades: 0,
            win_rate: 0,
            total_pnl: 0,
          });
        }
      } catch (error) {
        console.error('❌ Error fetching stats:', error);
        setStats({
          total_analyses: usage?.count || 0,
          total_trades: 0,
          win_rate: 0,
          total_pnl: 0,
        });
      }
    };

    fetchStats();
  }, [user, usage]);

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setPasswordStatus({ type: 'error', message: 'New passwords do not match' });
      return;
    }

    if (passwordForm.newPassword.length < 6) {
      setPasswordStatus({ type: 'error', message: 'Password must be at least 6 characters' });
      return;
    }

    setPasswordLoading(true);
    setPasswordStatus(null);

    try {
      const { error: verifyError } = await supabase.auth.signInWithPassword({
        email: user.email,
        password: passwordForm.currentPassword
      });

      if (verifyError) {
        setPasswordStatus({ type: 'error', message: 'Current password is incorrect' });
        return;
      }

      const { error: updateError } = await supabase.auth.updateUser({
        password: passwordForm.newPassword
      });

      if (updateError) {
        setPasswordStatus({ type: 'error', message: updateError.message });
      } else {
        setPasswordStatus({ type: 'success', message: 'Password updated successfully!' });
        setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
        setTimeout(() => {
          setShowPasswordModal(false);
          setPasswordStatus(null);
        }, 2000);
      }
    } catch (error) {
      setPasswordStatus({ type: 'error', message: 'An unexpected error occurred' });
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleStartTrading = () => navigate('/trading');
  const handleViewCharts = () => navigate('/trading');
  const handleSettings = () => setShowPasswordModal(true);
  const handleHistory = () => navigate('/trading');

  const handleProfileUpdate = () => {
    // Refresh profile data after update
    window.location.reload();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  // If loading is done but no profile yet, try to refresh or show temporary state
  if (!profile && user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-400">Checking permissions...</p>
        </div>
      </div>
    );
  }

  if (!profile && !user) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <SafeIcon icon={FiX} className="text-red-500 text-4xl mb-4 mx-auto" />
          <p className="text-white text-lg">Please log in to access the dashboard</p>
          <button 
            onClick={() => navigate('/login')}
            className="mt-4 px-6 py-2 bg-purple-600 hover:bg-purple-500 text-white rounded-lg"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  // Brand-new users: show the 3-step onboarding before the dashboard.
  if (profile?.first_login && user) {
    return <OnboardingFlow userId={user.id} onComplete={() => window.location.reload()} />;
  }

  const getSubscriptionBadge = () => {
    if (isAdmin) return { text: 'Admin Access', bgColor: 'bg-red-500/10', borderColor: 'border-red-500/30', textColor: 'text-red-400' };
    
    const planId = profile?.subscription_plan_id;
    const billingType = profile?.billing_type;
    const isInTrial = profile?.is_in_trial;
    
    // Check billing type for lifetime
    const lifetimeSuffix = billingType === 'lifetime' ? ' (Lifetime)' : '';
    const trialSuffix = isInTrial ? ' (Trial)' : '';
    
    switch (planId) {
      case 'enterprise':
        return { text: `Enterprise${lifetimeSuffix}`, bgColor: 'bg-amber-500/10', borderColor: 'border-amber-500/30', textColor: 'text-amber-400' };
      case 'institutional_ai':
        return { text: `Institutional AI${lifetimeSuffix}${trialSuffix}`, bgColor: 'bg-emerald-500/10', borderColor: 'border-emerald-500/30', textColor: 'text-emerald-400' };
      case 'pro_trader':
        return { text: `Pro Trader${lifetimeSuffix}${trialSuffix}`, bgColor: 'bg-violet-500/10', borderColor: 'border-violet-500/30', textColor: 'text-violet-400' };
      case 'starter':
        return { text: `Starter${lifetimeSuffix}`, bgColor: 'bg-blue-500/10', borderColor: 'border-blue-500/30', textColor: 'text-blue-400' };
      default:
        // If in trial without plan, show trial status
        if (isInTrial) {
          return { text: 'Trial (Institutional AI)', bgColor: 'bg-purple-500/10', borderColor: 'border-purple-500/30', textColor: 'text-purple-400' };
        }
        return { text: 'Free Plan', bgColor: 'bg-gray-500/10', borderColor: 'border-gray-500/30', textColor: 'text-gray-400' };
    }
  };

  const badge = getSubscriptionBadge();

  return (
    <div className="min-h-screen bg-gray-950">
      {/* Subtle gradient background */}
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/20 via-gray-950 to-cyan-950/20 pointer-events-none" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Trial Banner */}
        <TrialBanner />
        
        {/* Header - Modern 2026 */}
        <motion.header
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="relative flex-shrink-0">
                <div className="absolute inset-0 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 rounded-xl blur-lg opacity-50"></div>
                <div className="relative w-14 h-14 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 overflow-hidden flex items-center justify-center">
                  {profile?.avatar_url ? (
                    <img 
                      src={profile.avatar_url} 
                      alt="Profile" 
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <SafeIcon icon={FiUser} className="text-2xl text-cyan-400" />
                  )}
                </div>
                {isAdmin && (
                  <div className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 flex items-center justify-center ring-2 ring-gray-950">
                    <SafeIcon icon={FiShield} className="text-white text-xs" />
                  </div>
                )}
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-2xl lg:text-3xl font-semibold text-white truncate">
                  Welcome, {profile?.first_name || profile?.name || user?.email?.split('@')[0] || 'User'}
                </h1>
                <div className="flex items-center gap-3 mt-1">
                  <span className={`text-sm font-medium px-3 py-1 rounded-full ${badge.bgColor} border ${badge.borderColor} ${badge.textColor}`}>
                    {badge.text}
                  </span>
                  <span className="text-gray-500 text-sm hidden sm:inline">
                    {new Date().toLocaleDateString('en-US', { 
                      weekday: 'short', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <button
                onClick={() => setShowProfileModal(true)}
                className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 transition-all flex items-center gap-2 text-sm font-medium shadow-lg shadow-purple-500/20"
              >
                <div className="p-1 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/30">
                  <SafeIcon icon={FiUser} className="w-3.5 h-3.5 text-cyan-400" />
                </div>
                <span className="hidden sm:inline">Profile</span>
              </button>

              {/* Verification / KYC button removed — Snap Chart AI does not
                  require KYC (no custody of funds). The modal component and
                  admin panel remain intact for reactivation the day a
                  regulated feature (payouts, crypto conversion) ships. */}

              <button
                onClick={() => setShowSecurityModal(true)}
                className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 transition-all flex items-center gap-2 text-sm font-medium shadow-lg shadow-purple-500/20"
              >
                <div className="p-1 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/30">
                  <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-cyan-400" />
                </div>
                <span className="hidden sm:inline">Security</span>
              </button>

              <button
                onClick={() => navigate('/account/billing')}
                className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 transition-all flex items-center gap-2 text-sm font-medium shadow-lg shadow-purple-500/20"
              >
                <div className="p-1 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/30">
                  <SafeIcon icon={FiDollarSign} className="w-3.5 h-3.5 text-cyan-400" />
                </div>
                <span className="hidden sm:inline">Billing</span>
              </button>

              {/* Data Room removed from user-facing navbar — accessible via
                  the Admin panel only. Reversible by re-adding the block. */}

              {/* CTO Tasks moved to Admin panel only. */}
              
              {isAdmin && (
                <button
                  onClick={() => setShowAdminTools(!showAdminTools)}
                  className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 text-cyan-400 transition-all flex items-center gap-2 text-sm font-medium shadow-lg shadow-purple-500/20"
                >
                  <div className="p-1 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/30">
                    <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-cyan-400" />
                  </div>
                  <span className="hidden sm:inline">Admin</span>
                </button>
              )}
            </div>
          </div>
        </motion.header>

        {/* Admin Tools Panel */}
        <AnimatePresence>
          {showAdminTools && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="mb-8 overflow-hidden"
            >
              <div className="p-5 rounded-2xl bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 backdrop-blur-sm">
                <h3 className="text-sm font-semibold text-cyan-400 mb-4 flex items-center gap-2">
                  <div className="p-1 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/30">
                    <SafeIcon icon={FiShield} className="w-3.5 h-3.5 text-cyan-400" />
                  </div>
                  Administrator Tools
                </h3>
                <div className="grid grid-cols-1 gap-3">
                  <Link
                    to="/admin"
                    className="p-4 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 hover:from-purple-600/30 hover:to-cyan-600/30 border border-purple-500/20 hover:border-purple-500/40 transition-all group"
                  >
                    <div className="p-1.5 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/30 w-fit mb-2">
                      <SafeIcon icon={FiUsers} className="w-4 h-4 text-cyan-400" />
                    </div>
                    <div className="font-medium text-white text-sm">User Management</div>
                    <div className="text-xs text-gray-400">Manage roles & permissions</div>
                  </Link>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Platform Status Card - 2026 Design */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8"
        >
          <div className="p-6 lg:p-8 rounded-3xl bg-gradient-to-br from-gray-900/80 to-gray-900/40 border border-white/5 backdrop-blur-xl">
            {/* Header */}
            <div className="flex items-start justify-between mb-8">
              <div>
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  <span className="text-emerald-400 text-sm font-medium">System Status: Online</span>
                </div>
                <h2 className="text-2xl lg:text-3xl font-semibold text-white">
                  Snap Chart AI - BETA
                </h2>
                <p className="text-gray-400 mt-1">
                  Institutional-Grade Trading Intelligence Platform
                </p>
              </div>
            </div>

            {/* Features Grid - Premium Design */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="p-5 rounded-2xl bg-gray-900/60 border border-purple-500/20 hover:border-purple-500/40 transition-all">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="p-2 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                    <SafeIcon icon={FiClock} className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span className="text-sm font-medium tracking-wide">
                    <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Market Prices (Real-Time)</span>
                  </span>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Gold • Indices • Crypto
                </p>
              </div>
              
              <div className="p-5 rounded-2xl bg-gray-900/60 border border-cyan-500/20 hover:border-cyan-500/40 transition-all">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="p-2 rounded-xl bg-gradient-to-r from-cyan-600/20 to-purple-600/20 border border-cyan-500/30">
                    <SafeIcon icon={FiCpu} className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span className="text-sm font-medium tracking-wide">
                    <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">AI Analysis Engine</span>
                  </span>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Institutional • Voice • Charts
                </p>
              </div>
              
              <div className="p-5 rounded-2xl bg-gray-900/60 border border-purple-500/20 hover:border-purple-500/40 transition-all">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="p-2 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                    <SafeIcon icon={FiBook} className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span className="text-sm font-medium tracking-wide">
                    <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Trading Journal</span>
                  </span>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed">
                  TDI Score • Discipline • Proof
                </p>
              </div>

              <div className="p-5 rounded-2xl bg-gray-900/60 border border-cyan-500/20 hover:border-cyan-500/40 transition-all">
                <div className="flex items-center gap-2.5 mb-3">
                  <div className="p-2 rounded-xl bg-gradient-to-r from-cyan-600/20 to-purple-600/20 border border-cyan-500/30">
                    <SafeIcon icon={FiShield} className="w-4 h-4 text-cyan-400" />
                  </div>
                  <span className="text-sm font-medium tracking-wide">
                    <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Risk Management</span>
                  </span>
                </div>
                <p className="text-gray-400 text-xs leading-relaxed">
                  Capital • Limits • Rules
                </p>
              </div>
            </div>
          </div>
        </motion.section>

        {/* Stats Grid - Journal Style */}
        {stats && stats.total_analyses === 0 ? (
          // Empty state — big CTA instead of zero-value stats cards
          <motion.section
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mb-6"
          >
            <div className="relative overflow-hidden rounded-2xl border border-purple-500/30 bg-gradient-to-br from-purple-900/40 via-gray-900/80 to-cyan-900/30 p-8 sm:p-12 text-center">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(168,85,247,0.18),transparent_60%)] pointer-events-none" />
              <div className="relative">
                <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-purple-600/30 to-cyan-600/30 border border-purple-500/40 flex items-center justify-center">
                  <SafeIcon icon={FiZap} className="text-3xl text-cyan-300" />
                </div>
                <h2 className="text-2xl sm:text-3xl font-bold text-white mb-2">Run your first analysis in 30 seconds</h2>
                <p className="text-gray-400 mb-6 max-w-md mx-auto">
                  Snap a chart screenshot — get an institutional-grade verdict (TRADE / WAIT / NO-TRADE) with discipline built-in.
                </p>
                <button
                  onClick={() => navigate('/trading')}
                  className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-500 hover:to-cyan-500 text-white font-semibold text-base shadow-lg shadow-purple-600/30 transition-all hover:scale-105"
                >
                  <SafeIcon icon={FiZap} />
                  Run my first analysis
                  <SafeIcon icon={FiChevronRight} />
                </button>
                <div className="text-xs text-gray-500 mt-4">Your free credits are ready — no setup needed.</div>
              </div>
            </div>
          </motion.section>
        ) : (
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2 }}
          className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6"
        >
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="p-5 rounded-2xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 transition-all backdrop-blur-sm"
          >
            <div className="text-2xl lg:text-3xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{stats?.total_analyses || 0}</span>
            </div>
            <div className="text-xs mt-1 uppercase tracking-wider">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Completed Analyses</span>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.23 }}
            className="p-5 rounded-2xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 transition-all backdrop-blur-sm"
          >
            <div className="text-2xl lg:text-3xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">{stats?.total_trades || 0}</span>
            </div>
            <div className="text-xs mt-1 uppercase tracking-wider">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Executed Trades</span>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.26 }}
            className="p-5 rounded-2xl bg-gray-900/80 border border-purple-500/20 hover:border-purple-500/40 transition-all backdrop-blur-sm"
          >
            <div className="text-2xl lg:text-3xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">{stats?.win_rate || 0}%</span>
            </div>
            <div className="text-xs mt-1 uppercase tracking-wider">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Trade Win Rate</span>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.29 }}
            className="p-5 rounded-2xl bg-gray-900/80 border border-cyan-500/20 hover:border-cyan-500/40 transition-all backdrop-blur-sm"
          >
            <div className="text-2xl lg:text-3xl font-bold tracking-tight">
              <span className={`${(stats?.total_pnl || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                {(stats?.total_pnl || 0) >= 0 ? '+' : '-'}${Math.abs(stats?.total_pnl || 0).toLocaleString()}
              </span>
            </div>
            <div className="text-xs mt-1 uppercase tracking-wider">
              <span className="bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">Net P&L</span>
            </div>
          </motion.div>
        </motion.section>
        )}

        {/* 💰 Capital Saved by AI — only shows POSITIVE wins (decisions where the AI's
            NO-TRADE call was right). Hidden if user has no winning saves yet — never
            show a negative total (it's not "capital preserved", it's missed opportunities,
            which would kill conversion). */}
        {stats && stats.total_analyses > 0 && capitalPreserved && capitalPreserved.totalUsd > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.35 }}
            className="mb-6"
          >
            <div className="relative overflow-hidden rounded-2xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/30 via-gray-900/70 to-cyan-900/20 p-6 sm:p-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(16,185,129,0.18),transparent_60%)] pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
                <div>
                  <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-400 mb-2">
                    <SafeIcon icon={FiShield} className="w-4 h-4" />
                    Capital Saved by AI
                  </div>
                  <div className="flex items-baseline gap-3">
                    <span className="text-4xl sm:text-5xl font-bold text-emerald-400">
                      +${capitalPreserved.totalUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
                    </span>
                    <span className="text-lg font-semibold text-emerald-400/80">
                      (+{capitalPreserved.totalR.toFixed(1)}R)
                    </span>
                  </div>
                  <p className="text-sm text-gray-400 mt-2 max-w-md">
                    Losses avoided across <span className="text-white font-semibold">{capitalPreserved.decisions}</span> disciplined NO-TRADE / WAIT decisions where the AI&apos;s call was right.
                  </p>
                </div>
                <div className="text-right text-xs text-gray-500 italic max-w-[14rem]">
                  &quot;Anyone can take a trade. Professionals know when NOT to.&quot;
                </div>
              </div>
            </div>
          </motion.section>
        )}

        {/* 🌐 Your Viral Impact — only renders if the user has at least one
            share OR one inbound click attributed to them. Avoids showing an
            empty zero-card to fresh users. */}
        {stats && stats.total_analyses > 0 && viralStats && (viralStats.shares > 0 || viralStats.clicks > 0) && (
          <motion.section
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="mb-6"
          >
            <div className="relative overflow-hidden rounded-2xl border border-cyan-500/30 bg-gradient-to-br from-cyan-900/20 via-gray-900/70 to-purple-900/20 p-6 sm:p-8">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(34,211,238,0.15),transparent_60%)] pointer-events-none" />
              <div className="relative">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-cyan-400 mb-4">
                  <SafeIcon icon={FiShare2} className="w-4 h-4" />
                  Your Viral Impact
                </div>

                <div className="grid grid-cols-3 gap-4 sm:gap-6 mb-5">
                  <div>
                    <div className="text-3xl sm:text-4xl font-bold text-white">{viralStats.shares}</div>
                    <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Shares</div>
                  </div>
                  <div className="border-l border-white/10 pl-4 sm:pl-6">
                    <div className="text-3xl sm:text-4xl font-bold text-cyan-400">{viralStats.clicks}</div>
                    <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Visits</div>
                  </div>
                  <div className="border-l border-white/10 pl-4 sm:pl-6">
                    <div className={`text-3xl sm:text-4xl font-bold ${viralStats.signups > 0 ? 'text-emerald-400' : 'text-gray-500'}`}>{viralStats.signups}</div>
                    <div className="text-xs text-gray-400 mt-1 uppercase tracking-wider">Signups</div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-3 pt-4 border-t border-white/5">
                  <div className="flex-grow text-xs text-gray-400">
                    Your share link:&nbsp;
                    <code className="text-cyan-400 font-mono">snap-chart.ai/?ref={myShareCode || '...'}</code>
                  </div>
                  <button
                    onClick={copyMyShareLink}
                    disabled={!myShareCode}
                    className="px-4 py-2 bg-cyan-500/10 border border-cyan-500/30 rounded-lg text-cyan-400 text-sm font-medium hover:bg-cyan-500/20 transition disabled:opacity-50 whitespace-nowrap"
                  >
                    {shareLinkCopied ? '✓ Copied' : 'Copy link'}
                  </button>
                </div>
              </div>
            </div>
          </motion.section>
        )}

        {/* 💰 Affiliate Program CTA — visible to ALL logged-in users.
            Discreet banner, not pushy. Links to the self-service /affiliate page. */}
        <motion.section
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="mb-6"
        >
          <Link to="/affiliate" className="block group">
            <div className="relative overflow-hidden rounded-2xl border border-amber-500/20 bg-gradient-to-br from-amber-900/15 via-gray-900/60 to-emerald-900/15 p-5 sm:p-6 hover:border-amber-500/40 transition">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(245,158,11,0.12),transparent_60%)] pointer-events-none" />
              <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start gap-4">
                  <div className="hidden sm:flex w-12 h-12 rounded-xl bg-amber-500/20 border border-amber-500/30 items-center justify-center flex-shrink-0">
                    <SafeIcon icon={FiDollarSign} className="text-amber-400 w-6 h-6" />
                  </div>
                  <div>
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs uppercase tracking-wider text-amber-400 font-semibold">Affiliate Program</span>
                      <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-300 border border-amber-500/30">NEW</span>
                    </div>
                    <h3 className="text-lg font-bold text-white mb-1">Earn 30% recurring on every user you refer</h3>
                    <p className="text-sm text-gray-400">Paid monthly in USDT or PayPal. No cap, no expiry. Set up your wallet and start sharing.</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-center text-amber-400 text-sm font-medium whitespace-nowrap group-hover:translate-x-1 transition-transform">
                  Learn more <SafeIcon icon={FiChevronRight} />
                </div>
              </div>
            </div>
          </Link>
        </motion.section>

        {/* CTA - Full Width Premium */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
        >
          <button
            onClick={handleStartTrading}
            className="group w-full px-6 py-4 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 transition-all flex items-center justify-center gap-3 text-sm font-medium shadow-lg shadow-purple-500/20"
          >
            <div className="p-2 rounded-lg bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/30">
              <SafeIcon icon={FiCpu} className="w-5 h-5 text-cyan-400" />
            </div>
            <div className="text-center">
              <div className="text-base font-semibold text-cyan-400">
                Launch SnapChart AI
              </div>
              <div className="text-xs text-gray-400">
                Institutional-Grade Market Intelligence
              </div>
            </div>
          </button>
        </motion.div>

      </div>

      {/* Password Change Modal - 2026 Style */}
      <AnimatePresence>
        {showPasswordModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => setShowPasswordModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-gray-900 p-6 rounded-2xl border border-white/10 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <SafeIcon icon={FiKey} className="w-5 h-5 text-purple-400" />
                  Change Password
                </h3>
                <button
                  onClick={() => setShowPasswordModal(false)}
                  className="p-2 rounded-lg hover:bg-white/5 text-gray-400 hover:text-white transition-colors"
                >
                  <SafeIcon icon={FiX} className="w-5 h-5" />
                </button>
              </div>

              {passwordStatus && (
                <div className={`mb-4 p-4 rounded-xl ${
                  passwordStatus.type === 'success' 
                    ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-400'
                    : 'bg-red-500/10 border border-red-500/20 text-red-400'
                }`}>
                  {passwordStatus.message}
                </div>
              )}

              <form onSubmit={handlePasswordChange} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Current Password
                  </label>
                  <input
                    type="password"
                    required
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    New Password
                  </label>
                  <input
                    type="password"
                    required
                    minLength="6"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-400 mb-2">
                    Confirm New Password
                  </label>
                  <input
                    type="password"
                    required
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    className="w-full bg-white/5 border border-white/10 rounded-xl py-3 px-4 text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500/50 focus:border-transparent transition-all"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowPasswordModal(false)}
                    className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 text-gray-300 hover:from-purple-600/20 hover:to-cyan-600/20 hover:text-cyan-400 transition-all font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={passwordLoading}
                    className="flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 font-medium hover:from-purple-600/30 hover:to-cyan-600/30 hover:border-purple-500/50 disabled:opacity-50 transition-all flex items-center justify-center gap-2"
                  >
                    {passwordLoading ? (
                      <div className="w-5 h-5 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      'Update Password'
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Modal */}
      <ProfileModal
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
        userProfile={profile}
        onUpdate={handleProfileUpdate}
      />

      {/* Security Modal */}
      <SecurityModal
        isOpen={showSecurityModal}
        onClose={() => setShowSecurityModal(false)}
        userEmail={user?.email}
      />

      {/* KYC Verification Modal */}
      <KYCVerificationModal
        isOpen={showKYCModal}
        onClose={() => setShowKYCModal(false)}
      />
    </div>
  );
};

export default Dashboard;
